import React, { useState, useRef, useEffect } from 'react';
import Moment from 'moment';
import { toast } from '@rickylandino/react-messages';
import { Spinner } from 'react-bootstrap';
import { Tooltip } from 'antd';

import {
    searchPatients,
    getPatientLetters,
    getResultLetterUrl,
    getRecallLetterUrl,
    previewResultLetter,
    previewRecallLetter
} from '../../../common/services/next-gen/LetterVerificationService';

function ViewLetterVerification() {
    const SEARCH_LIMIT = 25;

    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [resultsCapped, setResultsCapped] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const [selectedPatient, setSelectedPatient] = useState(null);
    const [letters, setLetters] = useState([]);
    const [lettersLoaded, setLettersLoaded] = useState(false);
    const [lettersLoading, setLettersLoading] = useState(false);

    // Per-row loading states keyed by letter id
    const [urlLoadingId, setUrlLoadingId] = useState(null);
    const [previewLoadingId, setPreviewLoadingId] = useState(null);
    const [rowError, setRowError] = useState({});

    const searchDebounce = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(e) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        setSearchError('');

        if (searchDebounce.current) clearTimeout(searchDebounce.current);

        if (val.trim().length < 2) {
            setSearchResults([]);
            setResultsCapped(false);
            setShowDropdown(false);
            return;
        }

        searchDebounce.current = setTimeout(() => {
            runSearch(val.trim());
        }, 300);
    };

    const runSearch = async (q) => {
        setSearching(true);
        try {
            const results = await searchPatients(q, SEARCH_LIMIT);
            setSearchResults(results);
            setResultsCapped(results.length === SEARCH_LIMIT);
            setHighlightedIndex(-1);
            setShowDropdown(true);
        } catch (err) {
            if (err?.response?.status === 400) {
                setSearchError('Please enter a valid search term.');
            } else {
                toast.error('Failed to search patients.');
            }
            setShowDropdown(false);
        } finally {
            setSearching(false);
        }
    };

    const selectPatient = async (patient) => {
        setSelectedPatient(patient);
        setSearchTerm(`${patient.lName}, ${patient.fName}`);
        setShowDropdown(false);
        setSearchResults([]);
        setLetters([]);
        setLettersLoaded(false);
        setLettersLoading(true);
        setRowError({});

        try {
            const data = await getPatientLetters(patient.patientId);
            setLetters(data);
            setLettersLoaded(true);
        } catch {
            toast.error('Failed to load letters for this patient.');
            setLettersLoaded(true);
        } finally {
            setLettersLoading(false);
        }
    };

    const handleClear = () => {
        setSearchTerm('');
        setSelectedPatient(null);
        setLetters([]);
        setLettersLoaded(false);
        setSearchResults([]);
        setResultsCapped(false);
        setHighlightedIndex(-1);
        setRowError({});
    };

    const handleSeePrintedLetter = async (letter) => {
        setUrlLoadingId(letter.id);
        setRowError(prev => ({ ...prev, [letter.id]: null }));
        try {
            let result;
            if (letter.type === 'Result') {
                result = await getResultLetterUrl(letter.id);
            } else {
                result = await getRecallLetterUrl(letter.id);
            }
            window.open(result.url, '_blank');
        } catch {
            setRowError(prev => ({ ...prev, [letter.id]: 'Failed to retrieve the letter URL. Please try again.' }));
        } finally {
            setUrlLoadingId(null);
        }
    };

    const handlePreviewLetter = async (letter) => {
        setPreviewLoadingId(letter.id);
        setRowError(prev => ({ ...prev, [letter.id]: null }));
        try {
            let blob;
            if (letter.type === 'Result') {
                blob = await previewResultLetter(letter.id);
            } else {
                blob = await previewRecallLetter(letter.id);
            }
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            if (err?.response?.status === 403) {
                setRowError(prev => ({ ...prev, [letter.id]: 'You do not have access to this patient\'s records.' }));
            } else {
                setRowError(prev => ({ ...prev, [letter.id]: 'PDF generation failed. Please try again.' }));
            }
        } finally {
            setPreviewLoadingId(null);
        }
    };

    const printStatusIndicator = (letter) => {
        if (letter.hasS3Pdf) {
            return (
                <Tooltip title="PDF available in S3">
                    <span style={{ color: '#52c41a', fontWeight: 600 }}>
                        <i className="fas fa-check-circle" /> Printed (PDF available)
                    </span>
                </Tooltip>
            );
        }
        if (letter.hasPrintedLetter) {
            return (
                <Tooltip title="Printed in-app, but no S3 copy available">
                    <span style={{ color: '#faad14', fontWeight: 600 }}>
                        <i className="fas fa-print" /> Printed (no PDF)
                    </span>
                </Tooltip>
            );
        }
        return (
            <span style={{ color: '#8c8c8c' }}>
                <i className="fas fa-times-circle" /> Not Printed
            </span>
        );
    };

    const typeBadge = (type) => {
        const color = type === 'Result' ? '#1890ff' : '#52c41a';
        return (
            <span
                style={{
                    background: color,
                    color: '#fff',
                    borderRadius: 4,
                    padding: '2px 8px',
                    fontSize: 12,
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                }}
            >
                {type}
            </span>
        );
    };

    return (
        <div>
            <h4 className="mb-3">Letter Verification</h4>
            <p className="text-muted mb-4">
                Look up a patient and inspect their result and recall letter print history.
            </p>

            {/* Search */}
            <div className="row mb-3">
                <div className="col-md-6 position-relative" ref={dropdownRef}>
                    <div className="input-group">
                        <input
                            type="text"
                            className={`form-control${searchError ? ' is-invalid' : ''}`}
                            placeholder="Search by patient name or MRN..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onKeyDown={(e) => {
                                if (!showDropdown || searchResults.length === 0) return;
                                if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    setHighlightedIndex(i => Math.min(i + 1, searchResults.length - 1));
                                } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    setHighlightedIndex(i => Math.max(i - 1, 0));
                                } else if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
                                        selectPatient(searchResults[highlightedIndex]);
                                    }
                                } else if (e.key === 'Escape') {
                                    setShowDropdown(false);
                                    setHighlightedIndex(-1);
                                }
                            }}
                            autoComplete="off"
                        />
                        {searching && (
                            <div className="input-group-append">
                                <span className="input-group-text bg-white">
                                    <Spinner animation="border" size="sm" />
                                </span>
                            </div>
                        )}
                        {selectedPatient && (
                            <div className="input-group-append">
                                <button className="btn btn-outline-secondary" onClick={handleClear} type="button">
                                    <i className="fas fa-times" /> Clear
                                </button>
                            </div>
                        )}
                    </div>
                    {searchError && <div className="invalid-feedback d-block">{searchError}</div>}

                    {/* Dropdown results */}
                    {showDropdown && (
                        <div
                            className="border bg-white shadow-sm"
                            style={{
                                position: 'absolute',
                                zIndex: 1050,
                                width: '100%',
                                maxHeight: 300,
                                overflowY: 'auto',
                                borderRadius: 4
                            }}
                        >
                            {searchResults.length === 0 ? (
                                <div className="p-3 text-muted">No patients found.</div>
                            ) : (
                                <>
                                    {searchResults.map((p, index) => (
                                        <div
                                            key={p.patientId}
                                            className="px-3 py-2 hover"
                                            style={{
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #f0f0f0',
                                                background: highlightedIndex === index ? '#e6f7ff' : ''
                                            }}
                                            onClick={() => selectPatient(p)}
                                            onMouseEnter={() => setHighlightedIndex(index)}
                                            onMouseLeave={() => setHighlightedIndex(-1)}
                                        >
                                            <strong>{p.lName}, {p.fName}</strong>
                                            <span className="text-muted ml-2">MRN: {p.mrn}</span>
                                            <span className="text-muted ml-2">DOB: {Moment(p.dob).format('MM/DD/YYYY')}</span>
                                            <span className="text-muted ml-2">— {p.facilityName}</span>
                                        </div>
                                    ))}
                                    {resultsCapped && (
                                        <div
                                            className="px-3 py-2 text-muted"
                                            style={{ borderTop: '1px solid #f0f0f0', fontSize: 12, background: '#fafafa' }}
                                        >
                                            <i className="fas fa-info-circle mr-1" />
                                            Showing first {SEARCH_LIMIT} results — type more to narrow down.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Selected patient header */}
            {selectedPatient && (
                <div className="card mb-3">
                    <div className="card-body py-2">
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: 24 }}>
                            <div>
                                <span className="text-muted small">Patient</span>
                                <div className="font-weight-bold">{selectedPatient.lName}, {selectedPatient.fName}</div>
                            </div>
                            <div>
                                <span className="text-muted small">MRN</span>
                                <div>{selectedPatient.mrn}</div>
                            </div>
                            <div>
                                <span className="text-muted small">DOB</span>
                                <div>{Moment(selectedPatient.dob).format('MM/DD/YYYY')}</div>
                            </div>
                            <div>
                                <span className="text-muted small">Facility</span>
                                <div>{selectedPatient.facilityName}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Letter history table */}
            {selectedPatient && (
                <div>
                    {lettersLoading && (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                            <div className="mt-2 text-muted">Loading letters...</div>
                        </div>
                    )}

                    {lettersLoaded && letters.length === 0 && (
                        <div className="text-muted py-3">No letters found for this patient.</div>
                    )}

                    {lettersLoaded && letters.length > 0 && (
                        <div className="table-responsive">
                            <table className="table table-hover table-sm">
                                <thead className="thead-light">
                                    <tr>
                                        <th>Type</th>
                                        <th>Date</th>
                                        <th>Details</th>
                                        <th>Print Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {letters.map(letter => (
                                        <React.Fragment key={letter.id}>
                                            <tr>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    {typeBadge(letter.type)}
                                                </td>
                                                <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                                    {Moment(letter.date).format('MM/DD/YYYY')}
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    {letter.type === 'Result'
                                                        ? letter.recommendation
                                                        : letter.recallType}
                                                </td>
                                                <td style={{ verticalAlign: 'middle' }}>
                                                    {printStatusIndicator(letter)}
                                                </td>
                                                <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                                    {letter.hasS3Pdf && (
                                                        <Tooltip title="Open the stored PDF in a new tab">
                                                            <button
                                                                className="btn btn-sm btn-outline-secondary mr-2"
                                                                disabled={urlLoadingId === letter.id}
                                                                onClick={() => handleSeePrintedLetter(letter)}
                                                            >
                                                                {urlLoadingId === letter.id
                                                                    ? <Spinner animation="border" size="sm" />
                                                                    : <><i className="fas fa-file-pdf" /> See Printed Letter</>
                                                                }
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Previewing a letter is recorded in the audit log.">
                                                        <button
                                                            className="btn btn-sm btn-outline-primary"
                                                            disabled={previewLoadingId === letter.id}
                                                            onClick={() => handlePreviewLetter(letter)}
                                                        >
                                                            {previewLoadingId === letter.id
                                                                ? <Spinner animation="border" size="sm" />
                                                                : <><i className="fas fa-eye" /> Preview Letter</>
                                                            }
                                                        </button>
                                                    </Tooltip>
                                                </td>
                                            </tr>
                                            {rowError[letter.id] && (
                                                <tr>
                                                    <td colSpan={5}>
                                                        <div className="alert alert-danger alert-dismissible py-1 mb-0" role="alert">
                                                            {rowError[letter.id]}
                                                            <button
                                                                type="button"
                                                                className="close py-1"
                                                                onClick={() => setRowError(prev => ({ ...prev, [letter.id]: null }))}
                                                            >
                                                                <span>&times;</span>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

                    </div>
                );
            }

export default ViewLetterVerification;
